import type { FC } from 'react';
import { Link } from 'react-router';
import {
  getArtifactDisplayTitle,
  getArtifactStatusLabel,
  getArtifactStatusTone,
  getArtifactTypeIcon,
  getArtifactTypeLabel,
  shouldDisplayArtifactStatus,
} from '@gonfalon/observability-ui/components/Artifact';
import { Icon } from '@launchpad-ui/icons';

import { ArtifactCard } from './ArtifactCard';
import type { ConversationArtifact, ConversationArtifactSection } from './useConversationArtifactSections';

import * as styles from '../../../../internal/components/AgentInsights/components/artifacts/ConversationArtifactSections.css';

interface Props {
  projectId: string;
  projectKey: string;
  sections: ConversationArtifactSection[];
  density?: 'card' | 'compact';
}

const buildArtifactPath = (projectKey: string, type: string, externalId: string): string =>
  `/projects/${encodeURIComponent(projectKey)}/vega/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(externalId)}`;

const CompactArtifactPreview: FC<{
  projectKey: string;
  artifact: ConversationArtifact;
}> = ({ projectKey, artifact }) => {
  const typeLabel = getArtifactTypeLabel(artifact.type);
  const title = getArtifactDisplayTitle(artifact.type, artifact.title, artifact.external_id);
  const showStatus = shouldDisplayArtifactStatus(artifact.type, artifact.status);
  const statusLabel = showStatus && artifact.status ? getArtifactStatusLabel(artifact.status) : null;
  const statusTone = showStatus && artifact.status ? getArtifactStatusTone(artifact.status) : null;

  return (
    <Link
      to={buildArtifactPath(projectKey, artifact.type, artifact.external_id)}
      className={styles.compactRow}
      aria-label={`${typeLabel}: ${title}`}
    >
      <Icon name={getArtifactTypeIcon(artifact.type)} size="small" className={styles.compactIcon} />
      <span className={styles.compactType}>{typeLabel}</span>
      <span className={styles.compactTitle}>{title}</span>
      {statusLabel && statusTone ? <span className={styles.compactStatusTone[statusTone]}>{statusLabel}</span> : null}
    </Link>
  );
};

/**
 * Presentational grouped list of a conversation's artifacts — one labeled section per
 * non-empty group. Shared by the Artifacts tab pane and the tab's hover dropdown to keep grouping
 * consistent while each caller selects its presentation density. Stateless: callers decide loading
 * / empty / error handling and only pass non-trivial `sections` when there's content to show.
 */
export const ConversationArtifactSections: FC<Props> = ({ projectId, projectKey, sections, density = 'card' }) => (
  <div className={density === 'compact' ? styles.sectionsCompact : styles.sections}>
    {sections
      .filter((s) => s.artifacts.length > 0)
      .map((s) => (
        <section key={s.label} className={density === 'compact' ? styles.sectionCompact : styles.section}>
          <h3 className={styles.sectionLabel}>{s.label}</h3>
          <ul className={density === 'compact' ? styles.listCompact : styles.list}>
            {s.artifacts.map((a) => (
              <li key={a.id}>
                {density === 'compact' ? (
                  <CompactArtifactPreview projectKey={projectKey} artifact={a} />
                ) : (
                  <ArtifactCard
                    projectId={projectId}
                    projectKey={projectKey}
                    artifact={{
                      type: a.type,
                      externalId: a.external_id,
                      title: a.title,
                      status: a.status,
                      url: a.url,
                      updatedAt: a.updated_at,
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
  </div>
);
